import { useEffect, useState } from 'react';

/**
 * A modern hook for handling media queries
 * @param {string} query - The media query string
 * @returns {boolean} - Whether the media query matches
 */
export function useMediaQuery(query) {
   const [matches, setMatches] = useState(() => {
      // SSR-safe initialization
      if (typeof window === 'undefined') return false;
      return window.matchMedia(query).matches;
   });

   useEffect(() => {
      if (typeof window === 'undefined') return;

      const mediaQuery = window.matchMedia(query);

      // Set initial value
      if (mediaQuery.matches !== matches) {
         // eslint-disable-next-line react-hooks/set-state-in-effect
         setMatches(mediaQuery.matches);
      }

      // Modern event listener approach
      const handleChange = event => {
         setMatches(event.matches);
      };

      // Use modern addEventListener (supported in all modern browsers)
      mediaQuery.addEventListener('change', handleChange);

      return () => {
         mediaQuery.removeEventListener('change', handleChange);
      };
   }, [query, matches]);

   return matches;
}

/**
 * Predefined media query hooks for common breakpoints
 */
export const useIsMobile = () => useMediaQuery('(max-width: 768px)');
export const useIsTablet = () => useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 1025px)');
export const useIsNarrow = () => useMediaQuery('(max-width: 700px)');
export const useIsWide = () => useMediaQuery('(min-width: 1025px)');
